import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface MediaItem {
  src: string;
  alt: string;
  type?: 'image' | 'video';
}

interface TripGalleryProps {
  images: Array<MediaItem>;
}

export function TripGallery({ images }: TripGalleryProps) {
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>(Array(images.length).fill(false));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const videoRefs = useRef<{[key: number]: HTMLVideoElement | null}>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  // Set up intersection observer to detect when gallery is in viewport
  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.2, // 20% of the element must be visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsInView(entry.isIntersecting);
        
        // If gallery comes into view, play the current video
        if (entry.isIntersecting) {
          const currentVideo = videoRefs.current[currentSlide];
          if (currentVideo && images[currentSlide]?.type === 'video') {
            currentVideo.play().catch(err => {
              console.error("Error playing video on scroll into view:", err);
            });
          }
        } else {
          // If gallery goes out of view, pause all videos to save resources
          Object.values(videoRefs.current).forEach(video => {
            if (video) video.pause();
          });
        }
      });
    }, options);

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [currentSlide, images]);

  useEffect(() => {
    const mediaPromises = images.map((item, index) => {
      if (item.type === 'video') {
        setImagesLoaded(prev => {
          const updated = [...prev];
          updated[index] = true;
          return updated;
        });
        return Promise.resolve();
      }
      
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.src = item.src;
        img.onload = () => {
          setImagesLoaded(prev => {
            const updated = [...prev];
            updated[index] = true;
            return updated;
          });
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to preload image: ${item.src}`);
          // Initialize as loaded even on error to allow the component to render with placeholders
          setImagesLoaded(prev => {
            const updated = [...prev];
            updated[index] = true;
            return updated;
          });
          resolve();
        };
      });
    });

    Promise.all(mediaPromises);

    // Preload the first two videos for faster playback if available
    const videoItems = images
      .filter(item => item.type === 'video')
      .slice(0, 2);
    
    const preloadLinks: HTMLLinkElement[] = [];
    
    if (videoItems.length > 0) {
      videoItems.forEach(item => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'video';
        link.href = item.src;
        document.head.appendChild(link);
        preloadLinks.push(link);
      });
    }
    
    // Clean up when component unmounts
    return () => {
      preloadLinks.forEach(link => {
        if (link && document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [images]);

  // Function to handle video playback when slide changes
  const handleSlideChange = (swiper) => {
    setCurrentSlide(swiper.realIndex);
    
    // Pause all videos
    Object.values(videoRefs.current).forEach(videoElement => {
      if (videoElement) {
        videoElement.pause();
      }
    });
    
    // Only play video if gallery is currently in view
    if (isInView) {
      // Play the video on the current slide if it exists
      const currentVideo = videoRefs.current[swiper.realIndex];
      if (currentVideo && images[swiper.realIndex].type === 'video') {
        currentVideo.currentTime = 0;
        currentVideo.play().catch(err => {
          console.error("Error playing video:", err);
        });
      }
    }
  };

  return (
    <div className="my-16" ref={sectionRef}>
      {/* Section Heading */}
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-black mb-4">
          Trip Gallery
        </h2>
        <div className="w-24 h-1 bg-amber-700 mx-auto rounded-full mb-8"></div>
      </div>

      <div className="bg-white rounded-xl overflow-hidden border border-amber-100 shadow-md">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={0}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          speed={800}
          loop={true}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          onSlideChange={handleSlideChange}
          className="w-full h-[300px] sm:h-[400px] md:h-[500px]"
        >
          {images.map((item, index) => (
            <SwiperSlide key={index} className="relative">
              <div className="w-full h-full bg-amber-50 flex items-center justify-center">
                <div className={`absolute inset-0 transition-opacity duration-500 ${imagesLoaded[index] ? 'opacity-100' : 'opacity-0'}`}>
                  {item.type === 'video' ? (
                    <video
                      ref={el => { videoRefs.current[index] = el; }}
                      src={item.src}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="w-full h-full"
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <img
                      src={item.src}
                      alt={item.alt}
                      onError={(e) => { 
                        console.error(`Failed to load image: ${item.src}`); 
                        e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Safari+Image'; 
                      }}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className={`absolute inset-0 bg-amber-100 flex items-center justify-center transition-opacity duration-500 ${imagesLoaded[index] ? 'opacity-0' : 'opacity-100'}`}>
                  <div className="w-12 h-12 border-4 border-amber-700 border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <p className="text-white text-xl font-medium">{item.alt}</p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="mt-8 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {images.map((item, index) => (
          <div 
            key={index} 
            className={`relative cursor-pointer rounded-md overflow-hidden h-20 transition-all ${currentSlide === index ? 'ring-2 ring-amber-700' : 'opacity-70 hover:opacity-100'}`}
            onClick={() => {
              if(swiperRef.current) {
                swiperRef.current.slideToLoop(index, 800);
              }
            }}
          >
            {item.type === 'video' ? (
              <video 
                src={item.src} 
                className="w-full h-full object-cover"
              />
            ) : (
              <img 
                src={item.src} 
                alt={`Thumbnail ${index + 1}`}
                onError={(e) => { 
                  console.error(`Failed to load thumbnail: ${item.src}`); 
                  e.currentTarget.src = 'https://via.placeholder.com/150?text=Safari';
                }}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        ))}
      </div>

      {/* Custom styles for pagination bullets using a brown-ish orange color */}
      <style dangerouslySetInnerHTML={{ __html: `
        .swiper-pagination .swiper-pagination-bullet {
          background: rgb(192,91,42) !important;
        }
        .swiper-pagination .swiper-pagination-bullet-active {
          background: rgb(192,91,42) !important;
        }
        .swiper-button-next, .swiper-button-prev {
          color: rgb(192,91,42) !important;
        }
      ` }} />
    </div>
  );
}

export default TripGallery; 